import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const FIELDS = [
  ["company_name", "Company name"], ["tagline", "Tagline"],
  ["corp_office_label", "Office 1 label"], ["corp_office", "Office 1 address"],
  ["regd_office_label", "Office 2 label"], ["regd_office", "Office 2 address"],
  ["phone", "Phone"], ["email", "Email"], ["website", "Website"], ["gstin", "GSTIN"],
  ["pf_code", "PF Establishment Code"], ["esic_code", "ESIC Code"],
  ["signatory_title", "Signatory title"], ["payment_mode", "Payment mode"],
];
const RATES = [["pf_employee_pct", "PF employee %"], ["pf_employer_pct", "PF employer %"], ["esi_employee_pct", "ESI employee %"], ["esi_employer_pct", "ESI employer %"]];
const ATT_NUM = [["late_grace_min", "Late grace (min)"], ["late_tolerance_count", "Late marks forgiven / month"], ["early_grace_min", "Early-exit grace (min)"]];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function PayrollSettingsDialog({ onClose }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [alertInfo, setAlertInfo] = useState(null);

  useEffect(() => {
    api.get("/payroll/settings").then(({ data }) => setForm(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    api.get("/payroll/late-alerts").then(({ data }) => setAlertInfo(data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/payroll/settings", form);
      toast.success("Letterhead & statutory settings saved — slips will use them immediately");
      onClose?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-[760px]" data-testid="payroll-settings-dialog">
        <DialogHeader><DialogTitle className="text-sm">Salary Slip Letterhead &amp; Statutory Settings</DialogTitle></DialogHeader>
        {!form ? <div className="text-slate-400 text-sm py-6 text-center">Loading…</div> : (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {FIELDS.map(([k, label]) => (
                <label key={k} className={`block ${/office$/.test(k) ? "md:col-span-2" : ""}`}>
                  <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">{label}</div>
                  <Input value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="h-8 text-[12px]" data-testid={`payroll-setting-${k}`} />
                </label>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              {RATES.map(([k, label]) => (
                <label key={k} className="block">
                  <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">{label}</div>
                  <Input type="number" step="0.01" value={form[k] ?? ""} onChange={(e) => set(k, Number(e.target.value))} className="h-8 text-[12px] font-mono-num" data-testid={`payroll-setting-${k}`} />
                </label>
              ))}
            </div>
            <div className="border border-slate-200 rounded-sm p-2.5 bg-slate-50/60" data-testid="payroll-attendance-rules">
              <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-[#EA580C] mb-1.5">Attendance rules</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
                <label className="block">
                  <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">Shift in</div>
                  <Input type="time" value={form.shift_in ?? "09:30"} onChange={(e) => set("shift_in", e.target.value)} className="h-8 text-[12px] font-mono-num" data-testid="payroll-setting-shift_in" />
                </label>
                <label className="block">
                  <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">Shift out</div>
                  <Input type="time" value={form.shift_out ?? "18:00"} onChange={(e) => set("shift_out", e.target.value)} className="h-8 text-[12px] font-mono-num" data-testid="payroll-setting-shift_out" />
                </label>
                {ATT_NUM.map(([k, label]) => (
                  <label key={k} className="block">
                    <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">{label}</div>
                    <Input type="number" min={0} value={form[k] ?? 0} onChange={(e) => set(k, Number(e.target.value))} className="h-8 text-[12px] font-mono-num" data-testid={`payroll-setting-${k}`} />
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-4 flex-wrap mt-2 text-[11.5px] text-slate-700">
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!form.early_exit_deduction} onChange={(e) => set("early_exit_deduction", e.target.checked)} data-testid="payroll-setting-early_exit_deduction" /> Half-day cut for early exit too</label>
                <span className="flex items-center gap-1.5">Weekly offs:
                  {WEEKDAYS.map((d, i) => (
                    <button key={d} type="button" onClick={() => set("weekly_off_days", (form.weekly_off_days || []).includes(i) ? form.weekly_off_days.filter((x) => x !== i) : [...(form.weekly_off_days || []), i])}
                            className={`px-1.5 py-0.5 rounded-sm border text-[10.5px] font-semibold ${(form.weekly_off_days || []).includes(i) ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-slate-600 border-slate-200"}`} data-testid={`payroll-setting-weekoff-${d}`}>{d}</button>
                  ))}
                </span>
              </div>
              <div className="text-[10.5px] text-slate-500 mt-1.5">Arriving more than the grace period after shift-in = half-day deduction (after the forgiven count). Weekly offs & holidays are paid; L / A are LOP.</div>
            </div>
            <div className="border border-slate-200 rounded-sm p-2.5 bg-slate-50/60" data-testid="payroll-late-alert-rules">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
                <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-[#EA580C]">Morning late alert · {alertInfo?.schedule || "10:30 AM IST daily"}</div>
                <label className="flex items-center gap-1.5 text-[11.5px] text-slate-700"><input type="checkbox" checked={form.late_alert_enabled !== false} onChange={(e) => set("late_alert_enabled", e.target.checked)} data-testid="payroll-setting-late_alert_enabled" /> Enabled</label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                <label className="block">
                  <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">E-mail recipients <ChanState ok={alertInfo?.channels?.email} hint="add SENDGRID_API_KEY" /></div>
                  <Input value={form.late_alert_emails ?? ""} onChange={(e) => set("late_alert_emails", e.target.value)} placeholder="hr@celsius.co.in, md@celsius.co.in" className="h-8 text-[12px]" data-testid="payroll-setting-late_alert_emails" />
                </label>
                <label className="block">
                  <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">WhatsApp numbers <ChanState ok={alertInfo?.channels?.whatsapp} hint="add Twilio keys" /></div>
                  <Input value={form.late_alert_whatsapp ?? ""} onChange={(e) => set("late_alert_whatsapp", e.target.value)} placeholder="+919812345678, +9198…" className="h-8 text-[12px] font-mono-num" data-testid="payroll-setting-late_alert_whatsapp" />
                </label>
              </div>
              <div className="flex items-center gap-4 flex-wrap mt-2 text-[11.5px] text-slate-700">
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.late_alert_skip_offdays !== false} onChange={(e) => set("late_alert_skip_offdays", e.target.checked)} data-testid="payroll-setting-late_alert_skip_offdays" /> Skip Sundays &amp; holidays</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.late_alert_when_clear !== false} onChange={(e) => set("late_alert_when_clear", e.target.checked)} data-testid="payroll-setting-late_alert_when_clear" /> Also send "everyone on time" days</label>
              </div>
              <div className="text-[10.5px] text-slate-500 mt-1.5">Admins always get the digest in the ERP bell. E-mail / WhatsApp switch on automatically once the keys are set in the server .env.</div>
              {alertInfo?.preview && (
                <details className="mt-1.5">
                  <summary className="text-[10.5px] text-slate-600 cursor-pointer select-none" data-testid="payroll-late-alert-preview-toggle">Preview today's message ({alertInfo.preview.late_count} late)</summary>
                  <pre className="mt-1 text-[10.5px] whitespace-pre-wrap bg-white border border-slate-200 rounded-sm p-2 text-slate-700 max-h-40 overflow-auto" data-testid="payroll-late-alert-preview">{alertInfo.preview.text}</pre>
                </details>
              )}
            </div>
            <label className="block">
              <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">Footer note on slip</div>
              <Textarea rows={3} value={form.slip_note ?? ""} onChange={(e) => set("slip_note", e.target.value)} className="text-[12px]" data-testid="payroll-setting-slip_note" />
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="h-8 text-[12px]">Cancel</Button>
          <Button onClick={save} disabled={!form || saving} className="h-8 text-[12px] bg-[#0F172A] text-white hover:bg-slate-800" data-testid="payroll-settings-save">{saving ? "Saving…" : "Save settings"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChanState({ ok, hint }) {
  if (ok === undefined) return null;
  return <span className={`ml-1 normal-case tracking-normal font-medium ${ok ? "text-emerald-700" : "text-slate-400"}`}>{ok ? "· configured ✓" : `· not configured — ${hint}`}</span>;
}
